import {inject, Injectable} from '@angular/core';
import {Observable, timeout} from 'rxjs';
import {AuthApiService} from '../auth/auth-api.service';

export type ConnectServerConnection = {
  id: string;
  name: string;
  ref: string;
  status?: string;
  mode?: string;
  config?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
  expires_at?: string | null;
  oauth_connected?: boolean;
  connected?: boolean;
  provider_ref?: string;
};

export type ConnectConnectionPermissions = {
  connectionId?: string;
  providerRef?: string;
  connected?: boolean;
  scopeSet?: string | null;
  service?: string | null;
  source?: string;
  requestedScopes?: string[];
  grantedScopes?: string[];
  requiredScopes?: string[];
  missingScopes?: string[];
  hasRequiredScopes?: boolean;
};

export type ConnectConnectionPermissionsQuery = {
  service?: string;
  requiredScopes?: string[];
  scopeSet?: string | null;
  includeOptionalScopes?: boolean | null;
  extraScopes?: string[];
};

export type ConnectOAuthAuthorizationRequest = {
  provider_ref: string;
  client_id: string;
  connection_ref?: string;
  scope_set?: string;
  extra_scopes?: string[];
  include_optional_scopes?: boolean;
  browser_nonce?: string;
};

@Injectable({
  providedIn: 'root',
})
export class ConnectConnectionsService {
    private readonly api = inject(AuthApiService);

  private encodeId(id: string): string {
    return encodeURIComponent(String(id || '').trim());
  }

  authorize(request: ConnectOAuthAuthorizationRequest): Observable<{url: string}> {
    return this.api.post<{url: string}>('/api/v1/auth/authorize', request).pipe(timeout(10000));
  }

  list(): Observable<ConnectServerConnection[]> {
    return this.api.get<ConnectServerConnection[]>(`/api/v1/connections`);
  }

  get(id: string): Observable<ConnectServerConnection> {
    return this.api.get<ConnectServerConnection>(`/api/v1/connections/${this.encodeId(id)}`);
  }

  getPermissions(
    id: string,
    query?: ConnectConnectionPermissionsQuery,
  ): Observable<ConnectConnectionPermissions> {
    const params: Record<string, string> = {};
    if (query?.service) params['service'] = query.service;
    if (query?.requiredScopes?.length) params['required_scopes'] = query.requiredScopes.join(' ');
    if (query?.scopeSet) params['scope_set'] = query.scopeSet;
    if (typeof query?.includeOptionalScopes === 'boolean') {
      params['include_optional_scopes'] = query.includeOptionalScopes ? 'true' : 'false';
    }
    if (query?.extraScopes?.length) params['extra_scopes'] = query.extraScopes.join(' ');

    return this.api.get<ConnectConnectionPermissions>(
      `/api/v1/connections/${this.encodeId(id)}/permissions`,
      {params},
    );
  }

  create(connection: Omit<ConnectServerConnection, 'id'> & Partial<Pick<ConnectServerConnection, 'id'>>): Observable<ConnectServerConnection> {
    const body: any = {...connection, provider_ref: (connection as any).provider_ref || connection.ref};
    return this.api.post<ConnectServerConnection>(`/api/v1/connections`, body);
  }

  update(id: string, connection: Partial<ConnectServerConnection> & {name: string; ref: string}): Observable<ConnectServerConnection> {
    const body: any = {...connection, provider_ref: (connection as any).provider_ref || connection.ref};
    return this.api.put<ConnectServerConnection>(`/api/v1/connections/${this.encodeId(id)}`, body);
  }

  delete(id: string): Observable<void> {
    return this.api.delete<void>(`/api/v1/connections/${this.encodeId(id)}`);
  }
}
